<template>
  <v-container fluid class="elim-min pa-4">
    <v-row justify="center">
      <v-col cols="12">
        <v-card class="elim-card shadowCard" outlined>
          <v-card-title class="elim-head py-4 px-4 px-md-6 flex-column align-stretch">
            <div class="d-flex flex-wrap align-start justify-space-between">
              <div>
                <h1 class="text-h6 font-weight-medium mb-1">
                  Eliminación de adeudos
                </h1>
                <p class="text-body-2 text--secondary mb-0 elim-sub">
                  Ciclo obligatorio. La lista se actualiza al cambiar filtros y de forma periódica con un ciclo activo.
                </p>
              </div>
              <div class="caption text--secondary mt-2 mt-sm-0 text-sm-right">
                <v-icon x-small class="mr-1 grey--text" :class="{ 'elim-spin': cargar }">mdi-sync</v-icon>
                {{ ultimaActualizacionTexto }}
              </div>
            </div>
          </v-card-title>

          <v-divider />

          <v-card-text class="pa-4 pa-md-6">
            <Alerta :parametros="parametros" />

            <div class="mb-5">
              <div class="text-overline text--secondary mb-2" style="letter-spacing: 0.06em;">
                Estatus de adeudo
              </div>
              <v-chip-group
                v-model="filtroEstatusAdeudo"
                mandatory
                active-class="primary--text"
                @change="onFiltroEstatusChange"
              >
                <v-chip value="todos" filter outlined small>Todos</v-chip>
                <v-chip value="con" filter outlined small>Con adeudo</v-chip>
                <v-chip value="sin" filter outlined small>Sin adeudo</v-chip>
              </v-chip-group>
            </div>

            <v-row dense align="end">
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-autocomplete
                  v-model="filtroCiclo"
                  :items="ciclos"
                  label="Ciclo *"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-calendar-month"
                  item-text="ciclo"
                  item-value="id_ciclo"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-autocomplete
                  v-model="filtroPlantel"
                  :items="planteles"
                  label="Plantel"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-school-outline"
                  item-text="plantel"
                  item-value="id_plantel"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-text-field
                  v-model="textoBuscar"
                  label="Buscar"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-magnify"
                  placeholder="Nombre, matrícula…"
                  @keyup.enter="buscarAlumnos(1)"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-autocomplete
                  v-model="filtroGrupo"
                  :items="gruposFiltrados"
                  label="Grupo"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-account-group-outline"
                  item-text="grupo"
                  item-value="id_grupo"
                  :disabled="!filtroCiclo"
                />
              </v-col>
            </v-row>
            <v-row dense align="end">
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-autocomplete
                  v-model="filtroNivel"
                  :items="niveles"
                  label="Nivel"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-stairs"
                  item-text="nivel"
                  item-value="id_nivel"
                />
              </v-col>
              <v-col cols="12" sm="6" md="4" lg="3">
                <v-autocomplete
                  v-model="filtroHorario"
                  :items="horarios"
                  label="Horario"
                  outlined
                  dense
                  hide-details
                  clearable
                  prepend-inner-icon="mdi-clock-outline"
                  item-text="horario"
                  item-value="id_horario"
                />
              </v-col>
              <v-col cols="12" sm="6" md="8" lg="6" class="d-flex flex-wrap align-center pt-2">
                <v-btn
                  color="primary"
                  class="mr-2 mb-2"
                  text
                  small
                  :loading="cargar"
                  :disabled="!filtroCiclo"
                  @click="buscarAlumnos(1)"
                >
                  <v-icon left small>mdi-refresh</v-icon>
                  Actualizar
                </v-btn>
                <v-btn class="mb-2" text small :disabled="cargar" @click="limpiarFiltros">
                  <v-icon left small>mdi-filter-off</v-icon>
                  Limpiar filtros
                </v-btn>
              </v-col>
            </v-row>

            <p class="caption text--secondary mt-4 mb-0">
              Expandir fila para pagos y detalle de adeudo.
            </p>

            <v-data-table
              :headers="headers"
              :items="alumnos"
              :loading="cargar"
              :items-per-page="itemsPerPage"
              :expanded.sync="expanded"
              item-key="filaKey"
              show-expand
              single-expand
              hide-default-footer
              class="elevation-0 mt-2 elim-table"
              :mobile-breakpoint="0"
              no-data-text="No se encontraron alumnos con los filtros seleccionados"
            >
              <template v-slot:item.estatus_adeudo="{ item }">
                <span
                  class="text-caption font-weight-medium"
                  :class="alumnoTieneAdeudo(item) ? 'error--text' : 'success--text'"
                >
                  {{ alumnoTieneAdeudo(item) ? 'Con adeudo' : 'Sin adeudo' }}
                </span>
              </template>

              <template v-slot:item.precio_grupo_col="{ item }">
                <span class="body-2">{{ formatMoney(item.precio_grupo) }}</span>
              </template>

              <template v-slot:item.precio_desc_col="{ item }">
                <span class="body-2">{{ formatMoney(item.precio_con_descuento) }}</span>
              </template>

              <template v-slot:item.pagado_col="{ item }">
                <span class="body-2">{{ formatMoney(valorCampo(item, 'pagado')) }}</span>
              </template>

              <template v-slot:item.total_pagado_col="{ item }">
                <span class="body-2">{{ formatMoney(item.total_pagado) }}</span>
              </template>

              <template v-slot:item.adeudo_col="{ item }">
                <span
                  class="body-2"
                  :class="alumnoTieneAdeudo(item) ? 'font-weight-medium' : 'text--secondary'"
                >
                  {{ formatMoney(valorCampo(item, 'adeudo')) }}
                </span>
              </template>

              <template v-slot:expanded-item="{ headers, item }">
                <td :colspan="headers.length" class="elim-expanded pa-4">
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <div class="text-overline text--secondary mb-3" style="letter-spacing: 0.05em;">
                        Pagos
                      </div>
                      <v-list v-if="pagosList(item).length" dense two-line class="transparent pa-0">
                        <v-list-item v-for="(p, idx) in pagosList(item)" :key="idx" class="px-0">
                          <v-list-item-content>
                            <v-list-item-title class="body-2">
                              {{ formatMoney(p.monto != null ? p.monto : p.importe) }}
                              <span v-if="p.concepto || p.descripcion" class="caption text--secondary">
                                — {{ p.concepto || p.descripcion }}
                              </span>
                            </v-list-item-title>
                            <v-list-item-subtitle v-if="p.fecha || p.fecha_pago">
                              {{ p.fecha || p.fecha_pago }}
                            </v-list-item-subtitle>
                          </v-list-item-content>
                        </v-list-item>
                      </v-list>
                      <div v-else class="body-2 text--secondary">
                        <div class="mb-1">
                          <span class="text--secondary">Pagado:</span>
                          <span class="text--primary ml-1">{{ formatMoney(valorCampo(item, 'pagado')) }}</span>
                        </div>
                        <div class="mb-1">
                          <span class="text--secondary">Total pagado:</span>
                          <span class="text--primary ml-1">{{ formatMoney(valorCampo(item, 'total_pagado')) }}</span>
                        </div>
                        <span v-if="!tieneValorNumerico(valorCampo(item, 'pagado')) && !tieneValorNumerico(valorCampo(item, 'total_pagado'))" class="caption d-block mt-1">
                          El detalle por movimiento aparecerá cuando el API envíe el listado.
                        </span>
                      </div>
                    </v-col>
                    <v-col cols="12" md="6">
                      <div class="text-overline text--secondary mb-3" style="letter-spacing: 0.05em;">
                        Adeudo
                      </div>
                      <v-sheet rounded outlined class="pa-4 elim-resumen-adeudo">
                        <div class="d-flex justify-space-between align-center mb-1">
                          <span class="body-2 text--secondary">Adeudo</span>
                          <span class="text-h6 font-weight-medium">{{ formatMoney(valorCampo(item, 'adeudo')) }}</span>
                        </div>
                        <div v-if="tieneValorNumerico(item.precio_grupo)" class="d-flex justify-space-between caption text--secondary">
                          <span>Precio de grupo</span>
                          <span>{{ formatMoney(item.precio_grupo) }}</span>
                        </div>
                        <div v-if="tieneValorNumerico(item.precio_con_descuento)" class="d-flex justify-space-between caption text--secondary">
                          <span>Precio con descuento</span>
                          <span>{{ formatMoney(item.precio_con_descuento) }}</span>
                        </div>
                        <div v-if="tieneValorNumerico(item.descuento)" class="d-flex justify-space-between caption text--secondary">
                          <span>Descuento</span>
                          <span>{{ formatMoney(item.descuento) }}</span>
                        </div>
                        <p v-if="notaPrecioDescuento(item)" class="caption text--secondary mb-0 mt-2">
                          {{ notaPrecioDescuento(item) }}
                        </p>
                        <div v-if="item.detalle_adeudo || item.observaciones_adeudo" class="mt-2 body-2">
                          {{ item.detalle_adeudo || item.observaciones_adeudo }}
                        </div>
                        <div v-else-if="item.motivo_adeudo" class="mt-2 body-2">
                          {{ item.motivo_adeudo }}
                        </div>
                      </v-sheet>
                    </v-col>
                  </v-row>
                </td>
              </template>

              <template v-slot:item.acciones="{ item }">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn icon small v-bind="attrs" v-on="on" @click="abrirHistorial(item)">
                      <v-icon small>mdi-history</v-icon>
                    </v-btn>
                  </template>
                  <span>Historial</span>
                </v-tooltip>

                <v-btn
                  v-if="!alumnoTieneAdeudo(item)"
                  small
                  text
                  color="primary"
                  class="mr-1"
                  @click="abrirModal(item, 'agregar')"
                >
                  Agregar
                </v-btn>
                <v-btn
                  v-else
                  small
                  text
                  color="error"
                  @click="abrirModal(item, 'eliminar')"
                >
                  Eliminar
                </v-btn>
              </template>
            </v-data-table>

            <v-row v-if="totalAlumnos > itemsPerPage" class="mt-2" justify="center">
              <v-pagination
                v-model="page"
                :length="Math.ceil(totalAlumnos / itemsPerPage) || 1"
                :total-visible="7"
                @input="onPageInput"
              />
            </v-row>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    <!-- Modal confirmación -->
    <v-dialog v-model="dialogModal" max-width="520px" persistent>
      <v-card>
        <v-card-title class="text-subtitle-1">
          {{ accionModal === 'agregar' ? 'Agregar adeudo' : 'Eliminar adeudo' }}
        </v-card-title>
        <v-card-text v-if="filaSeleccionada">
          <p class="mb-2">
            <strong>Alumno:</strong> {{ filaSeleccionada.nombre }}
          </p>
          <p class="mb-2"><strong>Matrícula:</strong> {{ filaSeleccionada.matricula }}</p>
          <p class="mb-4"><strong>Grupo:</strong> {{ filaSeleccionada.grupo }}</p>
          <v-textarea
            v-model="motivoModal"
            label="Motivo *"
            outlined
            dense
            rows="3"
            hide-details="auto"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="guardando" @click="cerrarModal">Cancelar</v-btn>
          <v-btn
            color="primary"
            :loading="guardando"
            :disabled="guardando || !((motivoModal || '').trim())"
            depressed
            @click="confirmarAccion"
          >
            Confirmar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Historial -->
    <v-dialog v-model="dialogHistorial" max-width="800px">
      <v-card>
        <v-card-title class="text-subtitle-1">
          Historial de adeudos
        </v-card-title>
        <v-card-text>
          <v-progress-linear v-if="cargarHistorial" indeterminate />
          <template v-else>
            <v-sheet
              v-if="historialUltimo"
              outlined
              rounded
              class="pa-3 mb-4 elim-hist-resumen"
            >
              <div class="text-overline text--secondary mb-1" style="letter-spacing: 0.05em;">
                Última operación registrada
              </div>
              <div class="body-2">
                <strong>{{ historialUltimo.usuario_nombre || '—' }}</strong>
                <span class="text--secondary"> · </span>
                <span>{{ formatHistorialDia(historialUltimo.fecha_accion) }}</span>
                <span class="text--secondary"> · </span>
                <span>{{ formatHistorialHora(historialUltimo.fecha_accion) }}</span>
              </div>
              <div class="caption text--secondary mt-1">
                Acción: {{ historialUltimo.accion }}
                <span v-if="historialUltimo.motivo"> · Motivo: {{ historialUltimo.motivo }}</span>
              </div>
            </v-sheet>
            <v-simple-table dense>
              <thead>
                <tr>
                  <th class="text-left">Día</th>
                  <th class="text-left">Hora</th>
                  <th class="text-left">Realizado por</th>
                  <th class="text-left">Acción</th>
                  <th class="text-left">Motivo</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="h in historialItems" :key="h.id">
                  <td class="text-no-wrap">{{ formatHistorialDia(h.fecha_accion) }}</td>
                  <td class="text-no-wrap">{{ formatHistorialHora(h.fecha_accion) }}</td>
                  <td>{{ h.usuario_nombre }}</td>
                  <td>{{ h.accion }}</td>
                  <td>{{ h.motivo }}</td>
                </tr>
                <tr v-if="!historialItems.length">
                  <td colspan="5" class="text-center grey--text">Sin registros</td>
                </tr>
              </tbody>
            </v-simple-table>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="dialogHistorial = false">Cerrar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import { mapGetters } from 'vuex'
import validarErrores from '@/mixins/validarErrores'
import Alerta from '@/components/alertas/Alerta.vue'

const BUSCAR_DEBOUNCE_MS = 380
const AUTO_REFRESH_MS = 42000

export default {
  name: 'EliminacionAdeudos',
  components: { Alerta },
  mixins: [validarErrores],

  data () {
    return {
      planteles: [],
      ciclos: [],
      gruposCatalogo: [],
      niveles: [],
      horarios: [],

      filtroPlantel: null,
      filtroCiclo: null,
      filtroGrupo: null,
      filtroNivel: null,
      filtroHorario: null,
      filtroEstatusAdeudo: 'todos',
      textoBuscar: '',

      alumnos: [],
      totalAlumnos: 0,
      page: 1,
      itemsPerPage: 25,
      expanded: [],

      cargar: false,

      headers: [
        { text: 'Nombre', value: 'nombre' },
        { text: 'Matrícula', value: 'matricula' },
        { text: 'Plantel', value: 'plantel_nombre' },
        { text: 'Grupo', value: 'grupo' },
        { text: 'Nivel', value: 'nivel_nombre' },
        { text: 'Horario', value: 'horario' },
        { text: 'Precio grupo', value: 'precio_grupo_col', sortable: false },
        { text: 'Precio c/desc.', value: 'precio_desc_col', sortable: false },
        { text: 'Pagado', value: 'pagado_col', sortable: false },
        { text: 'Total pagado', value: 'total_pagado_col', sortable: false },
        { text: 'Adeudo', value: 'adeudo_col', sortable: false },
        { text: 'Estatus', value: 'estatus_adeudo', sortable: false },
        { text: 'Acciones', value: 'acciones', sortable: false, align: 'right', width: '260px' },
      ],

      dialogModal: false,
      accionModal: 'agregar',
      filaSeleccionada: null,
      motivoModal: '',
      guardando: false,

      dialogHistorial: false,
      historialItems: [],
      cargarHistorial: false,

      buscarDebounceTimer: null,
      autoRefreshTimer: null,
      ultimaActualizacion: null,
    }
  },

  computed: {
    ...mapGetters('login', ['getEscuela', 'getdatosUsuarioERPERP']),

    nombreUsuarioSesion () {
      const u = this.getdatosUsuarioERPERP
      if (!u || typeof u !== 'object') return ''
      return (
        u.nombre_completo ||
        u.nombre_usuario ||
        [u.nombre, u.apellido_paterno, u.apellido_materno].filter(Boolean).join(' ') ||
        u.email ||
        ''
      )
    },

    /** Auditoría ordenada por fecha descendente: la más reciente es la primera. */
    historialUltimo () {
      if (!this.historialItems || !this.historialItems.length) return null
      return this.historialItems[0]
    },

    gruposFiltrados () {
      const list = this.gruposCatalogo || []
      if (!this.filtroPlantel) return list
      return list.filter((g) => g.id_plantel === this.filtroPlantel)
    },

    ultimaActualizacionTexto () {
      if (!this.ultimaActualizacion) return 'Sin datos aún'
      try {
        return `Última actualización: ${this.ultimaActualizacion.toLocaleTimeString('es-MX', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
        })}`
      } catch (e) {
        return ''
      }
    },
  },

  watch: {
    filtroCiclo (val) {
      this.filtroGrupo = null
      this.cargarGruposPorCiclo()
      if (!val) {
        this.alumnos = []
        this.totalAlumnos = 0
        this.stopAutoRefresh()
        return
      }
      this.scheduleBuscar(1)
      this.startAutoRefresh()
    },
    filtroPlantel () {
      this.filtroGrupo = null
      this.scheduleBuscar(1)
    },
    filtroGrupo () {
      this.scheduleBuscar(1)
    },
    filtroNivel () {
      this.scheduleBuscar(1)
    },
    filtroHorario () {
      this.scheduleBuscar(1)
    },
    textoBuscar () {
      this.scheduleBuscar(1)
    },
    getEscuela (val) {
      if (val && this.filtroCiclo) {
        this.cargarGruposPorCiclo()
      }
    },
  },

  async created () {
    await this.cargarCatalogos()
    await this.cargarGruposPorCiclo()
  },

  beforeDestroy () {
    this.clearBuscarDebounce()
    this.stopAutoRefresh()
  },

  methods: {
    onFiltroEstatusChange () {
      this.scheduleBuscar(1)
    },

    onPageInput (p) {
      this.page = p
      this.buscarAlumnos()
    },

    scheduleBuscar (page) {
      if (!this.filtroCiclo) return
      this.clearBuscarDebounce()
      this.buscarDebounceTimer = setTimeout(() => {
        this.buscarDebounceTimer = null
        this.buscarAlumnos(page != null ? page : 1)
      }, BUSCAR_DEBOUNCE_MS)
    },

    clearBuscarDebounce () {
      if (this.buscarDebounceTimer) {
        clearTimeout(this.buscarDebounceTimer)
        this.buscarDebounceTimer = null
      }
    },

    startAutoRefresh () {
      this.stopAutoRefresh()
      if (!this.filtroCiclo) return
      this.autoRefreshTimer = setInterval(() => {
        if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return
        if (!this.filtroCiclo || this.cargar) return
        this.buscarAlumnos()
      }, AUTO_REFRESH_MS)
    },

    stopAutoRefresh () {
      if (this.autoRefreshTimer) {
        clearInterval(this.autoRefreshTimer)
        this.autoRefreshTimer = null
      }
    },

    filaKeyAlumno (row) {
      const id = row.id_alumno != null ? row.id_alumno : row.matricula
      const g = row.id_grupo != null ? row.id_grupo : ''
      return `${id}-${g}`
    },

    formatMoney (v) {
      if (v == null || v === '') return '—'
      const n = Number(v)
      if (Number.isNaN(n)) return String(v)
      return n.toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
    },

    tieneValorNumerico (v) {
      if (v == null || v === '') return false
      const n = Number(v)
      return !Number.isNaN(n)
    },

    montosAlumno (item) {
      const pick = (obj, keys) => {
        for (let i = 0; i < keys.length; i++) {
          const v = obj[keys[i]]
          if (v !== null && v !== undefined && v !== '') return v
        }
      }
      return {
        pagado: pick(item, ['pagado', 'pago_realizado', 'suma_pagos', 'pagado_grupo']),
        total_pagado: pick(item, ['total_pagado']),
        adeudo: pick(item, ['adeudo', 'monto_adeudo', 'diferencia', 'saldo_adeudo']),
        precio: pick(item, ['precio', 'precio_grupo', 'monto_grupo', 'costo']),
      }
    },

    /** Coherente con IF(adeudo>0) del API; corrige cadenas "0"/"1" de algunos drivers. */
    normalizarTieneAdeudo (row) {
      const t = row.tiene_adeudo
      if (t === true || t === 1) return 1
      if (t === false || t === 0) return 0
      if (typeof t === 'string') {
        const s = t.trim()
        if (s === '1' || s.toLowerCase() === 'true') return 1
        if (s === '0' || s === '') return 0
        const n = Number(s)
        if (n === 1) return 1
        if (n === 0 && !Number.isNaN(n)) return 0
      }
      const ad = Number(row.adeudo)
      return Number.isFinite(ad) && ad > 0 ? 1 : 0
    },

    alumnoTieneAdeudo (item) {
      return Number(item.tiene_adeudo) === 1
    },

    valorCampo (item, key) {
      const d = item[key]
      if (d !== null && d !== undefined && d !== '') return d
      if (d === 0) return 0
      const m = this.montosAlumno(item)
      if (key === 'pagado') return m.pagado
      if (key === 'adeudo') return m.adeudo
      if (key === 'total_pagado') return m.total_pagado
      return undefined
    },

    notaPrecioDescuento (item) {
      const pg = Number(item.precio_grupo)
      const d = Number(item.descuento)
      const pcd = Number(item.precio_con_descuento)
      if (!Number.isFinite(pg) || !Number.isFinite(pcd) || !Number.isFinite(d)) return null
      const simple = Math.round((pg - d) * 100) / 100
      if (Math.abs(simple - pcd) > 0.02) {
        return 'El precio con descuento lo calcula el sistema; puede no coincidir solo con precio de grupo menos este descuento (otros ajustes o beneficios).'
      }
      return null
    },

    parseFechaHistorial (raw) {
      if (raw instanceof Date && !Number.isNaN(raw.getTime())) return raw
      if (raw == null || raw === '') return null
      const s = String(raw).trim()
      const m = s.match(/^(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2})(?::(\d{2}))?/)
      if (m) {
        const y = parseInt(m[1], 10)
        const mo = parseInt(m[2], 10) - 1
        const day = parseInt(m[3], 10)
        const h = parseInt(m[4], 10)
        const mi = parseInt(m[5], 10)
        const sec = m[6] != null ? parseInt(m[6], 10) : 0
        return new Date(y, mo, day, h, mi, sec)
      }
      const d = new Date(s)
      return Number.isNaN(d.getTime()) ? null : d
    },

    formatHistorialDia (raw) {
      const d = this.parseFechaHistorial(raw)
      if (!d) return raw != null && raw !== '' ? String(raw) : '—'
      try {
        const base = d.toLocaleDateString('es-MX', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
          year: 'numeric',
        })
        return base.charAt(0).toUpperCase() + base.slice(1)
      } catch (e) {
        return String(raw)
      }
    },

    formatHistorialHora (raw) {
      const d = this.parseFechaHistorial(raw)
      if (!d) return '—'
      try {
        return d.toLocaleTimeString('es-MX', {
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        })
      } catch (e) {
        return '—'
      }
    },

    pagosList (item) {
      if (Array.isArray(item.pagos) && item.pagos.length) return item.pagos
      if (Array.isArray(item.pagos_grupo) && item.pagos_grupo.length) return item.pagos_grupo
      if (Array.isArray(item.historial_pagos) && item.historial_pagos.length) return item.historial_pagos
      return []
    },

    async cargarCatalogos () {
      this.cargar = true
      try {
        const [pl, ci, ni, ho] = await Promise.all([
          this.$http.get('planteles.activos'),
          this.$http.get('ciclos.activos.erp'),
          this.$http.get('niveles.activos'),
          this.$http.get('horarios.activos'),
        ])
        this.planteles = (pl.data || []).map((x) => ({
          ...x,
          id_plantel: x.id_plantel != null ? Number(x.id_plantel) : x.id_plantel,
        }))
        this.ciclos = (ci.data || []).map((x) => ({
          ...x,
          id_ciclo: x.id_ciclo != null ? Number(x.id_ciclo) : x.id_ciclo,
        }))
        this.niveles = ni.data || []
        this.horarios = ho.data || []
      } catch (error) {
        const msg =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Error al cargar catálogos'
        this.validarError(msg)
      } finally {
        this.cargar = false
      }
    },

    async cargarGruposPorCiclo () {
      if (!this.filtroCiclo || !this.getEscuela) {
        this.gruposCatalogo = []
        return
      }
      this.cargar = true
      try {
        const res = await this.$http.post('grupos.list', {
          id_ciclo: this.filtroCiclo,
          escuela: this.getEscuela,
        })
        this.gruposCatalogo = res.data || []
      } catch (error) {
        const msg =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Error al cargar grupos'
        this.validarError(msg)
        this.gruposCatalogo = []
      } finally {
        this.cargar = false
      }
    },

    validarFiltrosObligatorios () {
      if (!this.filtroCiclo) {
        this.validarError('Seleccione un ciclo.')
        return false
      }
      return true
    },

    async buscarAlumnos (pag) {
      if (pag != null) this.page = pag
      if (!this.validarFiltrosObligatorios()) return

      this.clearBuscarDebounce()
      this.cargar = true
      try {
        const params = {
          id_ciclo: Number(this.filtroCiclo),
          page: this.page,
          perPage: this.itemsPerPage,
        }
        if (this.filtroPlantel != null && this.filtroPlantel !== '') {
          params.id_plantel = Number(this.filtroPlantel)
        }
        if (this.filtroGrupo != null && this.filtroGrupo !== '') {
          params.id_grupo = Number(this.filtroGrupo)
        }
        if (this.filtroNivel != null && this.filtroNivel !== '') {
          params.id_nivel = Number(this.filtroNivel)
        }
        if (this.filtroHorario != null && this.filtroHorario !== '') {
          params.id_horario = Number(this.filtroHorario)
        }
        const buscar = (this.textoBuscar || '').trim()
        if (buscar) {
          params.buscar = buscar
        }
        if (this.filtroEstatusAdeudo === 'con') {
          params.tiene_adeudo = 1
        } else if (this.filtroEstatusAdeudo === 'sin') {
          params.tiene_adeudo = 0
        }

        const res = await this.$http.get('eliminacion-adeudos.alumnos', { params })
        const body = res.data || {}
        const rows = body.data || []
        this.alumnos = rows.map((row) => ({
          ...row,
          filaKey: this.filaKeyAlumno(row),
          tiene_adeudo: this.normalizarTieneAdeudo(row),
        }))
        this.totalAlumnos = body.total != null ? body.total : 0
        this.ultimaActualizacion = new Date()
      } catch (error) {
        const msg =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Error al consultar alumnos'
        this.validarError(msg)
        this.alumnos = []
        this.totalAlumnos = 0
      } finally {
        this.cargar = false
      }
    },

    limpiarFiltros () {
      this.filtroPlantel = null
      this.filtroCiclo = null
      this.filtroGrupo = null
      this.filtroNivel = null
      this.filtroHorario = null
      this.filtroEstatusAdeudo = 'todos'
      this.textoBuscar = ''
      this.gruposCatalogo = []
      this.alumnos = []
      this.totalAlumnos = 0
      this.page = 1
      this.expanded = []
      this.stopAutoRefresh()
    },

    abrirModal (item, accion) {
      this.filaSeleccionada = item
      this.accionModal = accion
      this.motivoModal = ''
      this.dialogModal = true
    },

    cerrarModal () {
      this.dialogModal = false
      this.guardando = false
      this.filaSeleccionada = null
      this.motivoModal = ''
    },

    async confirmarAccion () {
      const m = (this.motivoModal || '').trim()
      if (!m) {
        this.validarError('El motivo es obligatorio.')
        return
      }
      if (!this.filaSeleccionada) return

      this.guardando = true
      try {
        const res = await this.$http.put(
          `eliminacion-adeudos.adeudo/${this.filaSeleccionada.id_alumno}`,
          {
            id_grupo: this.filaSeleccionada.id_grupo,
            accion: this.accionModal,
            motivo: m,
          }
        )
        const msgBase = (res.data && res.data.message) || 'Operación correcta.'
        const quien = this.nombreUsuarioSesion || 'Usuario en sesión'
        const ahora = new Date()
        const detalle = `Registrado por ${quien} el ${this.formatHistorialDia(ahora)} a las ${this.formatHistorialHora(ahora)}.`
        this.validarSuccess(`${msgBase} ${detalle}`)
        this.cerrarModal()
        await this.buscarAlumnos()
      } catch (error) {
        const msg =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'Error al guardar'
        this.validarError(msg)
      } finally {
        this.guardando = false
      }
    },

    async abrirHistorial (item) {
      this.dialogHistorial = true
      this.cargarHistorial = true
      this.historialItems = []
      try {
        const res = await this.$http.get(`eliminacion-adeudos.auditoria/${item.id_alumno}`)
        this.historialItems = res.data || []
      } catch (error) {
        const msg =
          error.response && error.response.data && error.response.data.message
            ? error.response.data.message
            : 'No se pudo cargar el historial (¿tabla de auditoría creada?)'
        this.validarError(msg)
        this.historialItems = []
      } finally {
        this.cargarHistorial = false
      }
    },
  },
}
</script>

<style scoped>
.elim-min {
  background-color: #fafafa;
}
.elim-card {
  border-radius: 2px !important;
}
.elim-head {
  background: #fff;
}
.elim-sub {
  max-width: 640px;
}
.elim-table >>> tbody tr:hover {
  background: rgba(0, 0, 0, 0.02) !important;
}
.elim-expanded {
  background: #fff;
  border-bottom: thin solid rgba(0, 0, 0, 0.06) !important;
}
.elim-resumen-adeudo {
  background: #fafafa;
}
.elim-hist-resumen {
  background: #fafafa;
}
.elim-spin {
  animation: elim-spin 0.85s linear infinite;
}
@keyframes elim-spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}
</style>
